import { Sheet, SheetContent, SheetClose } from "@/v2-components/ui/sheet";
import CloseIcon from "@/v2-components/core/icon/close-icon";
import { useTrans } from "@/utils/useTrans";
import Image from "next/image";
import { useEffect, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Row = { label: string; values: [string, string, string, string] };

const inchRows: Row[] = [
  { label: "C", values: ['8"', '8.7"', '9.5"', '10.2"'] },
  { label: "L", values: ['2.7"', '3.1"', '3.4"', '3.6"'] },
];

const cmRows: Row[] = [
  { label: "C", values: ["20cm", "22cm", "24cm", "26cm"] },
  { label: "L", values: ["7cm", "7.8cm", "8.6cm", "9.2cm"] },
];

/**
 * Radix Sheet 의 `side` prop 은 정적이라 viewport 변경 시 동적으로 못 바꾼다.
 * mount 후 matchMedia 로 결정해 데스크톱=right (side sheet), 모바일=bottom (bottom sheet).
 * SSR 첫 렌더에선 sheet 닫혀있어 side mismatch 무관.
 */
function useSheetSide(): "right" | "bottom" {
  const [side, setSide] = useState<"right" | "bottom">("right");
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setSide(mq.matches ? "right" : "bottom");
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return side;
}

export default function Dk3SizeGuideSheet({ open, onOpenChange }: Props) {
  const { t } = useTrans(["tactsuit"]);
  const side = useSheetSide();
  const sizeLabels = [
    t("Small"),
    t("Medium"),
    t("Large"),
    t("X-Large"),
  ] as const;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className="flex flex-col gap-0 overflow-hidden bg-white p-0 text-coolgray-90 sm:max-w-none laptop:h-full laptop:w-[600px] laptop:max-w-[600px]"
      >
        {/* Header — sticky, body 만 스크롤 */}
        <div className="flex h-[84px] shrink-0 items-center gap-9 px-4 py-7 laptop:px-8">
          <h2 className="flex-1 text-[22px] font-400 leading-7 text-coolgray-90">
            {t("TactGlove DK3 Size Guide")}
          </h2>
          <SheetClose className="shrink-0 text-coolgray-90">
            <CloseIcon className="h-6 w-6" />
          </SheetClose>
        </div>

        {/* Body — scrollable */}
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-8 pt-5 laptop:gap-9 laptop:px-8 laptop:pt-9">
          {/* Image + measurement text */}
          <div className="flex flex-col items-stretch gap-5 laptop:flex-row laptop:gap-6">
            <div className="relative mx-auto flex aspect-square w-full max-w-[328px] shrink-0 items-center justify-center rounded-lg border border-coolgray-20 bg-white laptop:mx-0 laptop:size-[230px]">
              <div className="relative h-[80%] w-[78%]">
                <Image
                  src="/images/tactsuit/tactglove-dk3/size-guide-hand.png"
                  alt="Hand measurement diagram"
                  fill
                  className="object-contain"
                  sizes="(max-width: 1024px) 256px, 180px"
                />
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-4 laptop:gap-7">
              <p className="text-sm font-600 leading-[18px] tracking-[0.25px] text-coolgray-90 laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]">
                {t("Measure circumference and length of your dominant hand")}
              </p>
              <div className="flex flex-col gap-2 text-sm leading-[18px] tracking-[0.25px] laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]">
                <p className="font-600 text-coolgray-90">
                  {t("Circumference (C)")}
                </p>
                <p className="font-400 text-coolgray-60">
                  {t(
                    "Measure around the widest part of your hand (exclude thumb).",
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-2 text-sm leading-[18px] tracking-[0.25px] laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]">
                <p className="font-600 text-coolgray-90">{t("Length (L)")}</p>
                <p className="font-400 text-coolgray-60">
                  {t(
                    "Measure from the base of your middle finger (where it connects to your palm) to the tip of the finger.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Note */}
          <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
            {t(
              "If you are in between sizes, we recommend sizing down for a proper fit.",
            )}
          </p>

          {/* Tables */}
          <SizeTable
            headerLabel={t("inch Size")}
            sizeLabels={sizeLabels}
            rows={inchRows}
          />
          <SizeTable
            headerLabel={t("cm Size")}
            sizeLabels={sizeLabels}
            rows={cmRows}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

type TableProps = {
  headerLabel: string;
  sizeLabels: readonly [string, string, string, string];
  rows: Row[];
};

const SizeTable = ({ headerLabel, sizeLabels, rows }: TableProps) => (
  <div className="flex w-full border-b border-coolgray-20">
    {/* Label column */}
    <div className="flex flex-1 flex-col">
      <Cell variant="header">{headerLabel}</Cell>
      {rows.map((row) => (
        <Cell key={row.label}>{row.label}</Cell>
      ))}
    </div>
    {/* Value columns */}
    {sizeLabels.map((label, i) => (
      <div key={label} className="flex flex-1 flex-col">
        <Cell variant="header">{label}</Cell>
        {rows.map((row) => (
          <Cell key={row.label}>{row.values[i]}</Cell>
        ))}
      </div>
    ))}
  </div>
);

const Cell = ({
  variant,
  children,
}: {
  variant?: "header";
  children: React.ReactNode;
}) => (
  <div
    className={
      variant === "header"
        ? "flex items-center bg-coolgray-10 p-2 text-xs leading-4 tracking-[0.4px] text-coolgray-90 laptop:px-4 laptop:py-3 laptop:text-sm laptop:leading-[18px] laptop:tracking-[0.25px]"
        : "flex items-center p-2 text-xs leading-4 tracking-[0.4px] text-coolgray-90 laptop:px-4 laptop:py-5 laptop:text-sm laptop:leading-[18px] laptop:tracking-[0.25px]"
    }
  >
    {children}
  </div>
);
